/**
 * Registry: resolves content lookups.
 *
 * A civ unit/building either references a shared `base` (and overrides specific
 * attributes) or is a fully civ-unique def. Resolution is a SINGLE shallow merge
 * (deep-merging nested objects like cost/attack): `merge(base, civOverride)`.
 * No prototype chains, no separately-applied bonus multipliers — the civ file
 * states final values. Adding a civ/unit/building is data-only, no rebuild.
 */
import type {
  BuildingDef,
  CivBuildingEntry,
  CivDef,
  CivUnitEntry,
  GameData,
  MapDef,
  ResourceDef,
  ResourceNodeDef,
  UnitDef,
} from "./schema";

/** Deep-merge `override` onto `base` (objects merge, everything else replaces). */
function mergeInto<T>(base: T, override: Partial<T>): T {
  const out: Record<string, unknown> = Array.isArray(base)
    ? ([...(base as unknown[])] as unknown as Record<string, unknown>)
    : { ...(base as Record<string, unknown>) };
  for (const k of Object.keys(override as object)) {
    const bv = (base as Record<string, unknown>)[k];
    const ov = (override as Record<string, unknown>)[k];
    if (ov && typeof ov === "object" && !Array.isArray(ov) && bv && typeof bv === "object" && !Array.isArray(bv)) {
      out[k] = mergeInto(bv, ov as Partial<typeof bv>);
    } else {
      out[k] = ov;
    }
  }
  return out as T;
}

export class Registry {
  readonly data: GameData;
  private unitCache = new Map<string, UnitDef>();
  private buildingCache = new Map<string, BuildingDef>();

  constructor(data: GameData) {
    this.data = data;
  }

  resource(id: string): ResourceDef {
    return this.data.resources[id];
  }
  resourceNode(id: string): ResourceNodeDef {
    return this.data.resourceNodes[id];
  }
  civ(id: string): CivDef {
    const c = this.data.civs[id];
    if (!c) throw new Error(`Unknown civ: ${id}`);
    return c;
  }
  map(id: string): MapDef {
    return this.data.maps[id];
  }
  maps(): MapDef[] {
    return Object.values(this.data.maps);
  }

  /** Resolved unit for a civ. Cached. */
  unit(civId: string, unitId: string): UnitDef {
    const key = civId + "/" + unitId;
    const cached = this.unitCache.get(key);
    if (cached) return cached;
    const entry = this.civ(civId).units[unitId];
    if (!entry) throw new Error(`Unknown unit ${unitId} for civ ${civId}`);
    const baseId = entry.base;
    const over: CivUnitEntry = { ...entry };
    delete over.base;
    const base = baseId ? this.data.units[baseId] : undefined;
    if (baseId && !base) throw new Error(`Unit ${unitId} (${civId}) extends unknown base "${baseId}"`);
    const merged = base ? mergeInto(base, over as Partial<UnitDef>) : (over as UnitDef);
    const resolved: UnitDef = { ...merged, id: unitId, civ: civId };
    this.unitCache.set(key, resolved);
    return resolved;
  }

  /** All resolved units for a civ. */
  unitsOf(civId: string): UnitDef[] {
    return Object.keys(this.civ(civId).units).map((id) => this.unit(civId, id));
  }

  /** The civ's unit for a base/role (e.g. "worker") — matched by `base`, else by
   *  class. Used by map starting-entities and "train the basic worker". */
  roleUnit(civId: string, role: string): UnitDef | null {
    const civ = this.civ(civId);
    for (const id of Object.keys(civ.units)) if (civ.units[id].base === role) return this.unit(civId, id);
    for (const id of Object.keys(civ.units)) if (this.unit(civId, id).class === role) return this.unit(civId, id);
    return null;
  }

  /** Resolved units trained at a given building for a civ. */
  trainedAt(civId: string, buildingId: string): UnitDef[] {
    return this.unitsOf(civId).filter((u) => u.trainingBuilding === buildingId);
  }

  /** All shared and civ-unique buildings resolved for a civ, once each. */
  buildingsOf(civId: string): BuildingDef[] {
    const civBuildings = this.civ(civId).buildings ?? {};
    const ids = new Set([...Object.keys(this.data.buildings), ...Object.keys(civBuildings)]);
    return [...ids].map((id) => this.building(civId, id));
  }

  /** Resolved building def for a civ. Cached. */
  building(civId: string, buildingId: string): BuildingDef {
    const key = civId + "/" + buildingId;
    const cached = this.buildingCache.get(key);
    if (cached) return cached;
    const override = this.civ(civId).buildings?.[buildingId];
    const baseId = override?.base ?? buildingId;
    const base = this.data.buildings[baseId];
    if (!base && !override) throw new Error(`Unknown building: ${buildingId} (civ ${civId})`);
    if (override?.base && !base) throw new Error(`Building ${buildingId} (${civId}) extends unknown base "${override.base}"`);
    let resolved: BuildingDef;
    if (base) {
      if (override) {
        const over: CivBuildingEntry = { ...override };
        delete over.base;
        resolved = mergeInto(base, over as Partial<BuildingDef>);
      } else {
        resolved = { ...base };
      }
    } else {
      const over: CivBuildingEntry = { ...(override as CivBuildingEntry) };
      delete over.base;
      resolved = over as BuildingDef;
    }
    resolved = { ...resolved, id: buildingId };
    this.buildingCache.set(key, resolved);
    return resolved;
  }
}
